import { memo, useCallback } from 'react';
import { makeStyles, tokens, Text } from '@coreai/fluentui-react';
import {
  PlayFilled,
  ClockFilled,
  AppsFilled,
  CloudFilled,
  MoreHorizontalFilled,
  ArrowFlowUpRightFilled,
  ArrowSplitFilled,
  GlobeFilled,
  PeopleFilled,
  ChevronRightFilled,
  StarFilled,
  HeartFilled,
  BotFilled,
  MailFilled,
  DatabaseFilled,
  BriefcaseFilled,
  ShareFilled,
  CodeFilled,
  ServerFilled,
  PersonChatFilled,
} from '@fluentui/react-icons';
import type { BrowseCategory } from '@/services/operationsService';

type CategoryCardProps = {
  category: BrowseCategory;
  onClick: (categoryKey: string) => void;
};

const useStyles = makeStyles({
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    transitionProperty: 'background-color, box-shadow, transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    width: '100%',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      boxShadow: tokens.shadow4,
    },
    ':active': {
      transform: 'scale(0.97)',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
    },
  },
  iconContainer: {
    width: '44px',
    height: '44px',
    borderRadius: tokens.borderRadiusMedium,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: tokens.colorBrandBackground,
  },
  icon: {
    fontSize: tokens.fontSizeBase500,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  content: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
    // Clamp to a single line with an ellipsis so every card stays the same
    // (smaller) height regardless of how long the individual description copy is.
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  chevron: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
});

/**
 * Maps category icon names to Fluent UI icon components.
 * Add new entries here when new categories are introduced.
 */
const ICON_MAP: Readonly<Record<string, typeof AppsFilled>> = {
  Play: PlayFilled,
  Clock: ClockFilled,
  Apps: AppsFilled,
  Cloud: CloudFilled,
  MoreHorizontal: MoreHorizontalFilled,
  Flow: ArrowFlowUpRightFilled,
  Settings: ArrowSplitFilled,
  Globe: GlobeFilled,
  People: PeopleFilled,
  Star: StarFilled,
  Heart: HeartFilled,
  Bot: BotFilled,
  Mail: MailFilled,
  Database: DatabaseFilled,
  Briefcase: BriefcaseFilled,
  Share: ShareFilled,
  Code: CodeFilled,
  Server: ServerFilled,
  PersonChat: PersonChatFilled,
};

function getIconComponent(iconName: string) {
  const Icon = ICON_MAP[iconName] ?? AppsFilled;
  return <Icon />;
}

/**
 * Card component for browsing categories
 */
export const CategoryCard = memo(({ category, onClick }: CategoryCardProps) => {
  const styles = useStyles();

  const handleClick = useCallback(() => {
    onClick(category.key);
  }, [onClick, category.key]);

  const showChevron = category.type === 'browse' || category.type === 'favorites';

  return (
    <button
      className={styles.card}
      onClick={handleClick}
      aria-label={category.title}
    >
      <div className={styles.iconContainer}>
        <span className={styles.icon}>{getIconComponent(category.icon)}</span>
      </div>
      <div className={styles.content}>
        <Text className={styles.title}>{category.title}</Text>
        <Text className={styles.description} title={category.description}>
          {category.description}
        </Text>
      </div>
      {showChevron && (
        <ChevronRightFilled className={styles.chevron} />
      )}
    </button>
  );
});

export default CategoryCard;
